import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useTranslation } from 'hooks/useTranslation';
import config from 'config';
import Carousel from './Carousel';
import styles from './Carousel.module.scss';
import CarouselGrid from './CarouselGrid';
import FilterContext from './filterContext';
import type { Slide } from 'types/Utilities';
import type { APIBeforeAfterPhoto } from 'types/BeforeAfterPhotos';

type CarouselContainerProps = {
  counter?: boolean;
  itemVariant?: 'default' | 'before_after';
  limit?: number;
  loadAllPhotos?: boolean;
  loadMorePhotos?: boolean;
  onClose?: () => void;
  open?: boolean;
  pageType: 'clinic' | 'doctor' | 'treatment' | string;
  slides?: Slide[];
  slug: string;
  startSlide?: number;
  thumbnails?: boolean;
  totalCount?: string | number;
  useGrid?: boolean;
  variant?: 'default' | 'alternate';
  className?: string;
};

const CarouselContainer = ({
  counter = true,
  itemVariant = 'default',
  loadAllPhotos = true,
  loadMorePhotos = true,
  pageType,
  onClose,
  open = false,
  slides = [],
  slug,
  startSlide = 0,
  thumbnails = true,
  useGrid = true,
  variant = 'default',
  className,
  ...props
}: CarouselContainerProps): JSX.Element | null => {
  const { locale } = useTranslation();
  const { beforeAndAfterPhotosGridContainerLimit: limit } = config.defaults;

  const [isMobile, setIsMobile] = useState(false);
  const [isOpen] = useState(false || open);
  const [isCarouselOpen, setIsCarouselOpen] = useState(false);
  const [startSlideState, setStartSlideState] = useState(startSlide);
  const [carouselSlides, setCarouselSlides] = useState<Slide[]>(slides);
  const [filter, setFilter] = useState<Record<string, string>>({
    value: 'All',
    label: 'All Treatments',
  });

  useEffect(() => {
    setIsMobile(window.matchMedia('(max-width: 1023px)').matches);
  }, []);

  useEffect(() => {
    document.body.style.overflowY = 'hidden';
    document.body.classList.add('overflow-hidden');
    document.documentElement.classList.add('overflow-hidden');

    return () => {
      document.body.style.overflowY = '';
      document.body.classList.remove('overflow-hidden');
      document.documentElement.classList.remove('overflow-hidden');
    };
  }, [isOpen]);

  const onImageClick = (idx: number): void => {
    setStartSlideState(idx || 0);
    setIsCarouselOpen(true);
  };

  const onLoadMore = (slides: Slide[]): void => {
    setCarouselSlides(slides);
  };

  const doClose = (): void => {
    onClose && onClose();
  };

  /**
   * Retrieve more photos
   *
   * @param {number} [offset] - Number of photos to skip while fetching
   * @param {number} [limit] - Maximum number of photos to load with each call
   *
   * @returns {Promise<Record<string, { baphotos: APIBeforeAfterPhoto[] }>>}
   */
  const getPhotos = async (
    offset?: number,
    limit?: number,
    all?: boolean,
  ): Promise<Record<string, { baphotos: APIBeforeAfterPhoto[] }>> => {
    const headers = new Headers({
      'Content-Type': 'application/json',
    });

    const result = await fetch(
      `/api/media?slug=${slug}&recordType=${pageType}&locale=${locale}${
        offset ? `&offset=${offset}` : ''
      }${limit ? `&limit=${limit}` : ''}${all ? `&all=${all}` : ''}`,
      {
        headers,
      },
    );

    return await result.json();
  };

  const onFilterChange = (option: Record<string, string>): void => {
    setFilter(option);
  };

  return (
    <div
      className={clsx(
        'bg-white fixed top-0 left-0 w-full h-full z-50 opacity-100 pointer-events-auto pt-4 px-4 md:px-20 overflow-auto',
        styles['carousel'],
        className,
      )}
      style={{ zIndex: 99999999 }}
    >
      <FilterContext.Provider value={{ filter, setFilter }}>
        {useGrid && (
          <CarouselGrid
            itemVariant={itemVariant}
            counter={counter}
            onImageClick={onImageClick}
            onClose={doClose}
            slides={carouselSlides}
            isMobile={isMobile}
            onLoadMore={onLoadMore}
            getPhotos={getPhotos}
            getPhotosLimit={limit}
            getPhotosAll={loadAllPhotos}
            onFilterChange={onFilterChange}
            variant={variant}
            loadMorePhotos={loadMorePhotos}
            {...props}
          />
        )}
        {(useGrid && isCarouselOpen) || (!useGrid && isOpen) ? (
          <Carousel
            expand={useGrid ? isCarouselOpen : isOpen}
            itemVariant={itemVariant}
            thumbnails={thumbnails}
            counter={counter}
            onClose={() => {
              useGrid ? setIsCarouselOpen(false) : doClose();
            }}
            slides={carouselSlides}
            startSlide={startSlideState}
            isMobile={isMobile}
            variant={variant}
            locale={locale}
            onFilterChange={onFilterChange}
            {...props}
          />
        ) : null}
      </FilterContext.Provider>
    </div>
  );
};

export default CarouselContainer;
